/**
 * Performance Monitoring Hook - Bonapisca
 * Monitors animation performance, frame rates, and user experience metrics
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import i18next from 'i18next';

interface PerformanceMetrics {
  fps: number;
  frameDrops: number;
  averageFrameTime: number;
  worstFrameTime: number;
  memoryUsage?: number;
  animationDuration?: number;
  interactionLatency?: number;
}

interface PerformanceMonitorOptions {
  enabled?: boolean;
  sampleSize?: number;
  alertThreshold?: number;
  logToConsole?: boolean;
  trackMemory?: boolean;
  trackInteractions?: boolean;
}

interface UsePerformanceMonitoringReturn {
  metrics: PerformanceMetrics;
  isPerformanceGood: boolean;
  startMeasurement: (label: string) => () => void;
  measureInteraction: (interaction: string) => Promise<number>;
  getPerformanceReport: () => PerformanceReport;
  clearMetrics: () => void;
}

interface PerformanceReport {
  timestamp: number;
  metrics: PerformanceMetrics;
  recommendations: string[];
  deviceInfo: {
    userAgent: string;
    screenSize: string;
    pixelRatio: number;
    hardwareConcurrency: number;
    connection?: string;
  };
}

/**
 * Performance monitoring hook with comprehensive metrics tracking
 * Optimized for Material 3 animation performance monitoring
 */
export const usePerformanceMonitoring = (
  options: PerformanceMonitorOptions = {}
): UsePerformanceMonitoringReturn => {
  const {
    enabled = true,
    sampleSize = 60,
    alertThreshold = 30, // FPS threshold
    logToConsole = false,
    trackMemory = true,
    trackInteractions = true,
  } = options;

  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    frameDrops: 0,
    averageFrameTime: 16.67,
    worstFrameTime: 16.67,
    memoryUsage: 0,
    animationDuration: 0,
    interactionLatency: 0,
  });

  const frameTimesRef = useRef<number[]>([]);
  const lastFrameTimeRef = useRef<number>(performance.now());
  const rafIdRef = useRef<number | undefined>(undefined);
  const measurementsRef = useRef<Map<string, number>>(new Map());
  const interactionTimesRef = useRef<number[]>([]);

  // Frame rate monitoring
  const measureFrameRate = useCallback(() => {
    if (!enabled) return;

    const now = performance.now();
    const frameTime = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;

    // Add frame time to our samples
    frameTimesRef.current.push(frameTime);
    
    // Keep only recent samples
    if (frameTimesRef.current.length > sampleSize) {
      frameTimesRef.current.shift();
    }

    // Calculate metrics every few frames to avoid overhead - reduce frequency to prevent performance issues
    if (frameTimesRef.current.length % 30 === 0) {
      const frameTimes = frameTimesRef.current;
      const averageFrameTime = frameTimes.reduce((sum, time) => sum + time, 0) / frameTimes.length;
      const fps = Math.round(1000 / averageFrameTime);
      const worstFrameTime = Math.max(...frameTimes);
      const frameDrops = frameTimes.filter(time => time > 20).length; // Frames over 20ms

      const newMetrics: PerformanceMetrics = {
        fps,
        frameDrops,
        averageFrameTime: Math.round(averageFrameTime * 100) / 100,
        worstFrameTime: Math.round(worstFrameTime * 100) / 100,
        memoryUsage: trackMemory ? getMemoryUsage() : undefined,
        animationDuration: metrics.animationDuration,
        interactionLatency: getAverageInteractionLatency(),
      };

      setMetrics(newMetrics);

      // Log performance warnings - throttle to prevent spam
      if (logToConsole && fps < alertThreshold && frameTimesRef.current.length % 60 === 0) {
        console.warn(`🎭 Performance Alert: FPS dropped to ${fps} (threshold: ${alertThreshold})`);
      }

      // Log frame drops - throttle to prevent spam  
      if (logToConsole && frameDrops > 5 && frameTimesRef.current.length % 60 === 0) {
        console.warn(`🎭 Performance Alert: ${frameDrops} frame drops detected`);
      }
    }

    rafIdRef.current = requestAnimationFrame(measureFrameRate);
  }, [enabled, sampleSize, alertThreshold, logToConsole, trackMemory, metrics.animationDuration]);

  // Memory usage tracking
  const getMemoryUsage = (): number => {
    if ('memory' in performance && trackMemory) {
      const memory = (performance as any).memory;
      return Math.round((memory.usedJSHeapSize / 1024 / 1024) * 100) / 100; // MB
    }
    return 0;
  };

  // Interaction latency tracking
  const getAverageInteractionLatency = (): number => {
    if (interactionTimesRef.current.length === 0) return 0;
    const sum = interactionTimesRef.current.reduce((acc, time) => acc + time, 0);
    return Math.round((sum / interactionTimesRef.current.length) * 100) / 100;
  };

  // Start performance measurement for animations
  const startMeasurement = useCallback((label: string) => {
    if (!enabled) return () => {};

    const startTime = performance.now();
    measurementsRef.current.set(label, startTime);

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      setMetrics(prev => ({
        ...prev,
        animationDuration: duration,
      }));

      if (logToConsole) {
        console.log(`🎭 Animation "${label}" took ${Math.round(duration * 100) / 100}ms`);
      }

      measurementsRef.current.delete(label);
    };
  }, [enabled, logToConsole]);

  // Measure user interactions
  const measureInteraction = useCallback(async (interaction: string): Promise<number> => {
    if (!enabled || !trackInteractions) return 0;

    const startTime = performance.now();
    
    return new Promise((resolve) => {
      // Use next frame to measure interaction response
      requestAnimationFrame(() => {
        const endTime = performance.now();
        const latency = endTime - startTime;
        
        // Track interaction times
        interactionTimesRef.current.push(latency);
        if (interactionTimesRef.current.length > 20) {
          interactionTimesRef.current.shift();
        }

        if (logToConsole && latency > 16) {
          console.warn(`🎭 Slow Interaction: "${interaction}" took ${Math.round(latency * 100) / 100}ms`);
        }

        resolve(latency);
      });
    });
  }, [enabled, trackInteractions, logToConsole]);

  // Generate comprehensive performance report
  const getPerformanceReport = useCallback((): PerformanceReport => {
    const recommendations: string[] = [];

    // Generate recommendations based on metrics
    if (metrics.fps < 30) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.lowFps')));
    }
    
    if (metrics.frameDrops > 10) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.frameDrops')));
    }
    
    if (metrics.averageFrameTime > 20) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.frameTime')));
    }
    
    if (metrics.memoryUsage && metrics.memoryUsage > 100) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.memory')));
    }
    
    if (metrics.interactionLatency && metrics.interactionLatency > 50) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.latency')));
    }

    // Add device-specific recommendations
    const devicePixelRatio = window.devicePixelRatio || 1;
    const screenWidth = window.screen.width;
    
    if (devicePixelRatio > 2) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.highDpi')));
    }
    
    if (screenWidth < 768) {
      recommendations.push(String(i18next.t('common:hooks.performanceMonitoring.recommendations.mobile')));
    }

    return {
      timestamp: Date.now(),
      metrics,
      recommendations,
      deviceInfo: {
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        pixelRatio: devicePixelRatio,
        hardwareConcurrency: navigator.hardwareConcurrency || 1,
        connection: (navigator as any).connection?.effectiveType || 'unknown',
      },
    };
  }, [metrics]);

  // Clear all metrics
  const clearMetrics = useCallback(() => {
    frameTimesRef.current = [];
    interactionTimesRef.current = [];
    measurementsRef.current.clear();
    
    setMetrics({
      fps: 60,
      frameDrops: 0,
      averageFrameTime: 16.67,
      worstFrameTime: 16.67,
      memoryUsage: 0,
      animationDuration: 0,
      interactionLatency: 0,
    });
  }, []);

  // Determine if performance is good
  const isPerformanceGood = metrics.fps >= alertThreshold && metrics.frameDrops <= 5;

  // Start monitoring on mount
  useEffect(() => {
    if (enabled) {
      rafIdRef.current = requestAnimationFrame(measureFrameRate);
    }

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [enabled, measureFrameRate]);

  // Log initial device info
  useEffect(() => {
    if (enabled && logToConsole) {
      console.log('🎭 Performance Monitor initialized:', {
        screen: `${window.screen.width}x${window.screen.height}`,
        pixelRatio: window.devicePixelRatio,
        memory: trackMemory ? getMemoryUsage() + 'MB' : 'not tracked',
        cores: navigator.hardwareConcurrency || 'unknown',
      });
    }
  }, [enabled, logToConsole, trackMemory]);

  return {
    metrics,
    isPerformanceGood,
    startMeasurement,
    measureInteraction,
    getPerformanceReport,
    clearMetrics,
  };
};

/**
 * React component for displaying performance metrics (debug mode)
 */
export const PerformanceMonitor: React.FC<{
  enabled?: boolean;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  logToConsole?: boolean;
}> = ({ 
  enabled = false, 
  position = 'top-right',
  logToConsole = false,
}) => {
  const { metrics, isPerformanceGood } = usePerformanceMonitoring({ 
    enabled, 
    logToConsole,
  });

  if (!enabled) return null;

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-50 bg-black bg-opacity-75 text-white text-xs p-3 rounded-lg font-mono`}>
      <div className="space-y-1">
        <div className={`flex items-center gap-2 ${isPerformanceGood ? 'text-green-400' : 'text-red-400'}`}>
          <span>{isPerformanceGood ? '✅' : '⚠️'}</span>
          <span>FPS: {metrics.fps}</span>
        </div>
        <div>{i18next.t('common:performanceMonitor.frameTime')}: {metrics.averageFrameTime}ms</div>
        <div>{i18next.t('common:performanceMonitor.droppedFrames')}: {metrics.frameDrops}</div>
        {metrics.memoryUsage && <div>{i18next.t('common:performanceMonitor.memory')}: {metrics.memoryUsage}MB</div>}
        {metrics.interactionLatency !== undefined && (
          <div>{i18next.t('common:performanceMonitor.latency')}: {metrics.interactionLatency}ms</div>
        )}
      </div>
    </div>
  );
};

/**
 * HOC for automatic performance monitoring of components
 */
export const withPerformanceMonitoring = <P extends object>(
  Component: React.ComponentType<P>,
  monitorOptions?: PerformanceMonitorOptions
) => {
  const WrappedComponent = React.forwardRef<any, P>((props, ref) => {
    const { startMeasurement } = usePerformanceMonitoring(monitorOptions);

    useEffect(() => {
      const endMeasurement = startMeasurement(Component.displayName || Component.name || 'Component');
      return endMeasurement;
    }, [startMeasurement]);

    return React.createElement(Component, { ...(props as P), ref } as P);
  });

  WrappedComponent.displayName = `withPerformanceMonitoring(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
};
